<script setup lang="ts">
import { computed } from "vue"
import {useLocalePath} from "#i18n";
import {EVENT_TRACKING} from "~/constants/wadConstants";

const {trackEvent} = useTracking()
const { formatDuration } = useConverters()
const { getVideoMomentChaptersBlobUrl } = useVideo();
const localePath = useLocalePath()
const momentRef = ref<HTMLElement | null>(null)
const videoPlayer = ref<object>(null);
const startedPlaying = ref(false)

const props = withDefaults(defineProps<{
  moment: VideoMoment
  isOpen: boolean
  lazy?: boolean
}>(), {
  lazy: true,
});

const emit = defineEmits<{
  (e: 'momentOpened'): void
}>()

const trackingData = computed(() => ({video_id: props.moment.video_id, section_id: props.moment.id}));

const onPLayerReady = (player) => {
  videoPlayer.value = player;
  if(props.isOpen && !videoPlayer.value.state.playing) {
    videoPlayer.value.play();
  }
};

function playMoment() {
  if (!props.isOpen) {
    emit('momentOpened');
    if(videoPlayer.value && !videoPlayer.value.state.playing) {
      videoPlayer.value.play();
    }
  }
  if(!startedPlaying.value) {
    startedPlaying.value = true
    trackEvent(EVENT_TRACKING.VIDEO_MOMENT_STARTED, trackingData.value)
  }
}

watch(() => props.isOpen, async (newVal) => {
  if (newVal && momentRef.value) {
    await nextTick()

    const el = momentRef.value
    const parent = el?.closest('.wad4-moment-carousel__track')

    if (el && parent) {
      const parentRect = parent.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      const scrollLeft =
          parent.scrollLeft +
          (elRect.left - parentRect.left) -
          parentRect.width / 2 +
          elRect.width / 2

      parent.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      })
    }
  }
})

const videoPlaceholderImage = computed(() => props.moment.img_url.replace('_640x360', '_760x427?q=75'))
const chaptersBlobUrl = computed(() => {
  return getVideoMomentChaptersBlobUrl(props.moment.title, props.moment.starts_at, props.moment.starts_at + props.moment.duration);
});
const videoHref = computed(() => {
  return localePath({name: 'videos-id-slug', params: { id: props.moment.video_id, slug: props.moment.video_slug }})
})
const clipStartSec = computed(() => (props.moment.starts_at || 0) / 1000);

</script>

<template>
  <div class="wad4-video-moment" @pointerup="playMoment" :class="{'wad4-video-moment--player-visible': isOpen}" ref="momentRef">
    <img :src="moment.img_url" :alt="moment.title" :fetchpriority="lazy ? 'auto' : 'high'" :loading="lazy ? 'lazy' : 'eager'" class="wad4-video-moment__image"/>
    <div class="wad4-video-moment__overlay wad4-video-moment__blur-overlay"></div>
    <CommonEventIcon :eventCategory="props.moment.event_category" />
    <div class="wad4-video-moment__info">
      <div v-if="moment.duration" class="wad4-video-moment__duration-wrapper">
        <span class="wad4-video-moment__duration">{{ formatDuration(clipStartSec) }} MIN</span>
      </div>
      <h3 v-if="moment.title" class="wad4-video-moment__info-title">{{ moment.title }}</h3>
      <h4 v-if="moment.video_title" class="wad4-video-moment__info-subtitle">{{ moment.video_title }}</h4>
    </div>
    <client-only>
      <VideoPlayer class="wad4-video-moment__player"
                   ref="videoPlayerRef"
                   :key="moment.video_id"
                   :title="moment.video_title"
                   :posterUrl="videoPlaceholderImage"
                   :source="moment.cloudflare_video_url"
                   :chaptersUrl="chaptersBlobUrl"
                   :startTime="moment.starts_at"
                   :endTime="moment.starts_at + moment.duration"
                   :autoPip="false"
                   :videoHref="videoHref"
                   @playerReady="onPLayerReady"
      />
    </client-only>
  </div>
</template>

<style lang="scss">
.wad4-video-moment {
  position: relative;
  border-radius: $wad4-border-radius-md;
  overflow: hidden;
  height: 189px;
  width: 336px;
  flex-shrink: 0;
  cursor: pointer;
  @include mqBreakPoint($wad4-breakpoint-mobile-max, $wad4-breakpoint-tablet-max) {
    height: 240px;
  }
  @include mqBreakPoint($wad4-breakpoint-desktop-min) {
    height: 300px;
  }

  .wad4-event-icon {
    position: absolute;
    width: 24px;
    height: 24px;
    z-index: 1;
    top: $wad4-spacing-xl;
    left: $wad4-spacing-xl;
  }

  &--player-visible {
    flex: 0 0 336px !important;
    @include mqBreakPoint($wad4-breakpoint-mobile-max, $wad4-breakpoint-tablet-max) {
      flex: 0 0 427px !important;
    }
    @include mqBreakPoint($wad4-breakpoint-desktop-min) {
      flex: 0 0 533px !important;
    }
  }

  &__player {
    display: none;
    opacity: 0;
    position: absolute;
    pointer-events: none;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition-duration: 0.2s;
    transition-property: opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    .wad4-video-moment--player-visible & {
      display: block;
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__image {
    position: absolute;
    max-width: 100%;
    height: 100%;
    transition: transform 0.3s ease;
    object-fit: cover;
    border-radius: $wad4-border-radius-md;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $wad4-spacing-md;
    transition-delay: .2s;
    transition-property: opacity;
    transition-timing-function: cubic-bezier(.4,0,.2,1);
    transition-duration: .15s;
    position: absolute;
    bottom: 0;
    padding: 15px;
    color: #fff;

    &-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 1rem;
      line-height: 1.25rem;
      font-weight: $wad4-font-weight-semi-bold;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &-subtitle {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 19.25px;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__duration {
    color: #FFF;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.20);
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: 0.25px;
    text-transform: uppercase;

    &-wrapper {
      display: flex;
      padding: $wad4-spacing-xxs $wad4-spacing-md;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 50px;
      background: rgba(255, 255, 255, 0.15);
      backdrop-filter: blur(4px);
    }
  }

  &__blur-overlay {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    backdrop-filter: blur(12px);
    background-image: linear-gradient(oklab(0 0 0 / 0.4) 0%, oklab(0 0 0 / 0.2) 50%, rgba(0, 0, 0, 0.6) 100%);
    border: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    color-scheme: dark;
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }

  .wad4-moment-carousel__card:hover &__blur-overlay {
    backdrop-filter: none;
    background-image: linear-gradient(oklab(0 0 0 / 0.4) 0%, oklab(0 0 0 / 0.2) 50%, rgba(0, 0, 0, 0.6) 100%);
  }

  .vds-slider-chapter-title {
    font-weight: $wad4-font-weight-semi-bold;
  }

}
</style>
